import * as XLSX from 'xlsx-js-style'
import type { ChecklistTemplate, DeviceSheet, ParameterRecord, ProjectData } from './types'
import {
  buildDeviceSheets,
  buildFormalSheetRows,
  getBusinessRecords,
  type ManualEdits,
} from './workbookData'

type ExportScope = 'full' | 'filtered' | 'sheet'

const COLORS = {
  title: '2F5D82',
  header: '6E96BA',
  headerText: 'FFFFFF',
  text: '1F2937',
  border: 'B8C5D1',
  label: 'DCEAF7',
  name: 'EAF3FB',
  manual: 'F7F8FA',
  original: 'DDF4E4',
  partial: 'FFF2CC',
  inferred: 'DDEBFF',
  missing: 'FCE1E1',
  notApplicable: 'E7E6E6',
} as const

const border = {
  top: { style: 'thin' as const, color: { rgb: COLORS.border } },
  bottom: { style: 'thin' as const, color: { rgb: COLORS.border } },
  left: { style: 'thin' as const, color: { rgb: COLORS.border } },
  right: { style: 'thin' as const, color: { rgb: COLORS.border } },
}

function statusFill(value: unknown): string {
  if (value === '原文提取') return COLORS.original
  if (value === '部分找到') return COLORS.partial
  if (value === 'AI推理') return COLORS.inferred
  if (value === '不适用') return COLORS.notApplicable
  return COLORS.missing
}

function value(value: unknown): string | number {
  if (value == null) return ''
  if (typeof value === 'number') return value
  return String(value)
}

function safeFileName(value: string): string {
  return value.replace(/[\\/:*?"<>|]/gu, '_').replace(/\s+/gu, ' ').trim()
}

function cellStyle(fill: string, options: { bold?: boolean; color?: string; align?: 'left' | 'center' } = {}): XLSX.CellStyle {
  return {
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    font: { name: '微软雅黑', sz: 10, bold: options.bold || false, color: { rgb: options.color || COLORS.text } },
    alignment: { vertical: 'center', horizontal: options.align || 'left', wrapText: true },
    border,
    protection: { locked: true },
  } as XLSX.CellStyle
}

function manualStyle(): XLSX.CellStyle {
  return { ...cellStyle(COLORS.manual), protection: { locked: false } } as XLSX.CellStyle
}

function applyCell(sheet: XLSX.WorkSheet, row: number, column: number, cellValue: unknown, style: XLSX.CellStyle) {
  const address = XLSX.utils.encode_cell({ r: row, c: column })
  sheet[address] = { t: 's', v: value(cellValue), s: style } as XLSX.CellObject
}

function mergeRanges(template: ChecklistTemplate) {
  return template.merges.map((merge) => ({
    s: { r: merge.startRow, c: merge.startColumn },
    e: { r: merge.endRow - 1, c: merge.endColumn - 1 },
  }))
}

function createFormalSheet(
  template: ChecklistTemplate,
  built: ReturnType<typeof buildFormalSheetRows>,
): XLSX.WorkSheet {
  const data = built.rows.map((row) => row.map(value))
  const sheet = XLSX.utils.aoa_to_sheet(data)
  sheet['!merges'] = mergeRanges(template)
  sheet['!cols'] = [...template.columnWidths, 26].map((wch) => ({ wch }))
  sheet['!rows'] = data.map((_, row) => ({ hpt: Number(template.rowHeights[String(row)] || (row === 0 ? 26 : row === 1 ? 48 : 20)) }))
  sheet['!autofilter'] = { ref: 'A2:I120' }
  ;(sheet as XLSX.WorkSheet & { '!freeze'?: unknown })['!freeze'] = {
    xSplit: 3,
    ySplit: 2,
    topLeftCell: 'D3',
    activePane: 'bottomRight',
    state: 'frozen',
  }
  ;(sheet as XLSX.WorkSheet & { '!protect'?: unknown })['!protect'] = {}

  const maxRows = 120
  for (let row = 0; row < maxRows; row += 1) {
    const status = built.rowStyles[row] ? data[row]?.[4] : undefined
    for (let column = 0; column < 9; column += 1) {
      const fill = row === 0
        ? COLORS.title
        : row === 1
          ? COLORS.header
          : column === 0 || column === 1
            ? COLORS.label
            : column === 2
              ? COLORS.name
              : column === 8
                ? COLORS.manual
                : status ? statusFill(status) : 'FFFFFF'
      const style = row === 0
        ? cellStyle(COLORS.title, { bold: true, color: COLORS.headerText })
        : row === 1
          ? cellStyle(COLORS.header, { bold: true, color: COLORS.headerText, align: 'center' })
          : column === 8
            ? manualStyle()
            : cellStyle(fill, { bold: column < 2 })
      applyCell(sheet, row, column, data[row]?.[column] ?? '', style)
    }
  }
  sheet['!dataValidation'] = [
    { sqref: 'E3:E120', type: 'list', formula1: '"原文提取,部分找到,AI推理,未找到,不适用"' },
    { sqref: 'F3:F120', type: 'list', formula1: '"100%,95%,85%,75%,60%,<60%,—"' },
  ]
  return sheet
}

function sheetsForExport(project: ProjectData, records: ParameterRecord[]): DeviceSheet[] {
  const formalRecords = getBusinessRecords(project.provenance, project.checklistTemplate, records)
  return buildDeviceSheets(formalRecords)
}

export function exportBusinessWorkbook(
  project: ProjectData,
  records: ParameterRecord[],
  manualEdits: ManualEdits,
  scope: ExportScope,
  activeSheet?: string,
) {
  const allFormalRecords = getBusinessRecords(project.provenance, project.checklistTemplate)
  const visibleFormalRecords = getBusinessRecords(project.provenance, project.checklistTemplate, records)
  const deviceSheets = sheetsForExport(project, allFormalRecords)
  const workbook = XLSX.utils.book_new()
  const targetSheets = scope === 'sheet'
    ? deviceSheets.filter((sheet) => sheet.sheetName === activeSheet).slice(0, 1)
    : deviceSheets
  const finalSheets = targetSheets.length ? targetSheets : deviceSheets.slice(0, 1)

  finalSheets.forEach((deviceSheet) => {
    const visibleRecords = scope === 'full' ? allFormalRecords : visibleFormalRecords
    const built = buildFormalSheetRows(project.provenance, project.checklistTemplate, deviceSheet, visibleRecords, allFormalRecords, manualEdits)
    XLSX.utils.book_append_sheet(workbook, createFormalSheet(project.checklistTemplate, built), deviceSheet.sheetName)
  })

  const projectName = safeFileName(project.provenance.project.name || project.manifest.project_key)
  const suffix = scope === 'full' ? '全部Sheet' : scope === 'sheet' ? `当前-${safeFileName(finalSheets[0]?.sheetName || 'Sheet')}` : '当前筛选'
  XLSX.writeFile(workbook, `${projectName}_变压器点检表_${suffix}.xlsx`, { bookType: 'xlsx', compression: true })
}
